import { CALC_TYPES_BY_STYLE, PATTERN_OPTIONS, patternOptionsForStyle, SHAPE_OPTIONS, SYNC_FIELD_OPTIONS, AUTO_SYSTEM_OPTIONS, randomDrawableColor } from "../config/conditionCalcTypes";
import { AUTO_SYSTEM_LAYER_KEYS } from "../utils/autoLayoutEngine";

// The "Drawable on canvas" block of an Item / Assembly — Style, the three quantity readings, default height / width /
// thickness, color, pattern, shape, "replaces room field", generated system, tile grid, hatch spacing and rounding.
// ONE component, used by Setup ▸ Estimate Engine ▸ Items / Assemblies AND by the takeoff canvas's one-off condition
// form, so a one-off is configured with exactly the same choices as a catalog row (and a future field appears in both).
// `draft` carries the PascalCase Item fields; `hideAutoSystem` drops "Generated system" (a one-off must not become the
// symbol the HEPA / lighting engine generates for a revision).

const fieldLabel = { fontSize: 10, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.04em", display: "block", marginBottom: 2 };
const inputSm = { width: "100%", fontSize: 12, padding: "3px 6px", border: "1px solid var(--cs-border)", borderRadius: 4 };

function Field({ label, span, children }) {
  return (
    <div style={span ? { gridColumn: `span ${span}` } : undefined}>
      <label style={fieldLabel}>{label}</label>
      {children}
    </div>
  );
}

export default function DrawableOnCanvasFields({ draft, setDraft, layers = [], usedColors = [], hideAutoSystem = false }) {
  // Picking a Generated system defaults an unset Layer to the matching
  // seeded standard layer (db/67 std_hepa_filters / std_light_fixtures) so
  // generated symbols land where the direction doc says; still editable.
  const setAutoSystem = (value) => {
    const next = { ...draft, AutoSystem: value || null };
    if (value && !draft.LayerID) {
      const layer = layers.find(l => l.LayerKey === AUTO_SYSTEM_LAYER_KEYS[value] && l.IsActive);
      if (layer) next.LayerID = layer.LayerID;
    }
    setDraft(next);
  };
  const opts = CALC_TYPES_BY_STYLE[draft.Style] || [];
  return (
    <div style={{ gridColumn: "span 4", borderTop: "1px solid var(--cs-border)", paddingTop: 10, marginTop: 4 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text3)", marginBottom: 6 }}>
        DRAWABLE ON CANVAS — optional, lets this row be drawn/placed directly on the Room Takeoff canvas (Phase B)
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 }}>
          <Field label="Style">
            <select
              value={draft.Style || ""}
              onChange={e => {
                const style = e.target.value || null;
                setDraft({
                  ...draft, Style: style,
                  Quantity1CalcType: !style ? null : style === "count" ? "count" : style === "area" ? "area" : "length",
                  Quantity2CalcType: null, Quantity3CalcType: null,
                  // A row that just became drawable gets a random palette color
                  // (not the same teal every time) — re-roll with 🎲 or pick.
                  Color: draft.Color || (style ? randomDrawableColor(usedColors) : null),
                });
              }}
              style={inputSm}
            >
              <option value="">— not drawn —</option>
              <option value="linear">Linear</option>
              <option value="area">Area</option>
              <option value="count">Count</option>
            </select>
          </Field>
          {draft.Style && [1, 2, 3].map(n => (
            <Field key={n} label={`Quantity ${n}${n === 1 ? " (= UOM)" : ""}`}>
              <select
                value={draft[`Quantity${n}CalcType`] || ""}
                onChange={e => setDraft({ ...draft, [`Quantity${n}CalcType`]: e.target.value || null })}
                style={inputSm}
              >
                {n > 1 && <option value="">— none —</option>}
                {opts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </Field>
          ))}
        </div>
        {draft.Style && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 }}>
            <Field label="Default Height (ft)"><input type="number" step="0.1" value={draft.DefaultHeightFt} onChange={e => setDraft({ ...draft, DefaultHeightFt: e.target.value })} style={inputSm} /></Field>
            <Field label={draft.Style === "count" ? "Symbol Size / Diameter (ft)" : "Default Width (ft)"}>
              <input type="number" step="0.1" value={draft.DefaultWidthFt} onChange={e => setDraft({ ...draft, DefaultWidthFt: e.target.value })} style={inputSm}
                title={draft.Style === "count" ? "Placed size of the symbol: a circle's diameter, a rectangle/light module's long side, a door's opening width. Blank = 1.5 ft for a free-placed marker, 3 ft for a door." : "Width used by Volume/SF derivations (Width × Height)."} />
            </Field>
            {/* Thickness only feeds a Volume calc-type reading (calcQuantityValue,
                conditionCalcTypes.js) — offered on both Linear (length x height x
                thickness, e.g. a wall) and Area (footprint x thickness, e.g. a
                slab) styles; a Count item has no Volume option at all, so
                thickness is meaningless there. */}
            <Field label={`Default Thickness (in)${draft.Style === "count" ? " (not for count)" : ""}`}>
              <input type="number" step="0.1" disabled={draft.Style === "count"} value={draft.DefaultThicknessIn} onChange={e => setDraft({ ...draft, DefaultThicknessIn: e.target.value })} style={inputSm} />
            </Field>
            <Field label="Color">
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <input type="color" value={draft.Color || "#0d9488"} onChange={e => setDraft({ ...draft, Color: e.target.value })}
                  style={{ width: 40, height: 26, padding: 0, border: "1px solid var(--cs-border)", borderRadius: 4, cursor: "pointer" }} />
                <button type="button" onClick={() => setDraft({ ...draft, Color: randomDrawableColor([...usedColors, draft.Color]) })}
                  title="Random color from the palette (skips colors other drawable rows already use)"
                  style={{ fontSize: 13, background: "none", border: "1px solid var(--cs-border)", borderRadius: 4, cursor: "pointer", padding: "1px 6px" }}>🎲</button>
              </div>
            </Field>
            {/* One Pattern column, two meanings — a Linear run's stroke dash, or
                a FILL (OST's own hatch set) for an Area and for a Count symbol's
                glyph alike (per direct feedback 2026-09-11). See
                conditionCalcTypes.js. */}
            <Field label={draft.Style === "area" || draft.Style === "count" ? "Fill Pattern" : draft.Style === "linear" ? "Line Pattern" : "Pattern"}>
              <select value={draft.Pattern || ""} disabled={!patternOptionsForStyle(draft.Style)} onChange={e => setDraft({ ...draft, Pattern: e.target.value || null })} style={inputSm}>
                {(patternOptionsForStyle(draft.Style) || PATTERN_OPTIONS).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </Field>
            <Field label={`Shape${draft.Style !== "count" ? " (count only)" : ""}`}>
              <select value={draft.Shape || ""} disabled={draft.Style !== "count"} onChange={e => setDraft({ ...draft, Shape: e.target.value || null })} style={inputSm}>
                {SHAPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </Field>
            <Field label={`Replaces Room Field${draft.Style !== "count" && !draft.IsRoomElement ? " (count symbol or room element)" : ""}`}>
              {/* A count symbol writes the legacy field through from the canvas;
                  a Room Element tagged here supersedes the field for its readers
                  (outlet_count → receptacle circuits). */}
              <select value={draft.SyncsToCriteriaField || ""} disabled={draft.Style !== "count" && !draft.IsRoomElement} onChange={e => setDraft({ ...draft, SyncsToCriteriaField: e.target.value || null })} style={inputSm}>
                {SYNC_FIELD_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </Field>
            {!hideAutoSystem && (<>
            {/* HEPA & Lighting engine (autoLayoutEngine.js): tag THE Count item the
                engine places on the canvas for a system. A Room Template's own
                Standard Equipment row tagged the same way wins for its rooms. */}
            <Field label={`Generated system${draft.Style !== "count" ? " (count only)" : ""}`}>
              <select value={draft.AutoSystem || ""} disabled={draft.Style !== "count"} onChange={e => setAutoSystem(e.target.value)} style={inputSm}
                title="Marks this Count item as the symbol the HEPA / lighting engine generates on the Room Takeoff canvas from a room's effective count.">
                {AUTO_SYSTEM_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </Field>
            </>)}
          </div>
        )}
        {/* OST Condition Properties parity (db/30, 2026-09-11): an Area's tile
            grid (Advanced > Grid "Tile W x H, Gap" — enables the Tile count /
            Grid length calc types above and draws the grid over the area on
            canvas) and hatch Spacing; a Linear run's Round to (Advanced >
            "Round Quantity"). Blank = today's behavior. */}
        {(draft.Style === "area" || draft.Style === "count") && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 }}>
            {/* Tile grid is an Area concept; a Count symbol only gets the hatch spacing. */}
            <Field label={`Grid Tile Width (ft)${draft.Style !== "area" ? " (area only)" : ""}`}><input type="number" step="0.01" min="0" placeholder="e.g. 2" disabled={draft.Style !== "area"} value={draft.GridTileWFt ?? ""} onChange={e => setDraft({ ...draft, GridTileWFt: e.target.value })} style={inputSm} title="Tile width for the Tile count / Grid length calc types and the on-canvas grid overlay. Leave blank for no grid." /></Field>
            <Field label="Grid Tile Height (ft)"><input type="number" step="0.01" min="0" placeholder="e.g. 4" disabled={draft.Style !== "area"} value={draft.GridTileHFt ?? ""} onChange={e => setDraft({ ...draft, GridTileHFt: e.target.value })} style={inputSm} /></Field>
            <Field label="Grid Gap (in)"><input type="number" step="0.125" min="0" placeholder="0" disabled={draft.Style !== "area"} value={draft.GridGapIn ?? ""} onChange={e => setDraft({ ...draft, GridGapIn: e.target.value })} style={inputSm} title="Space between tiles (e.g. a T-bar flange). Tile pitch = tile + gap." /></Field>
            <Field label={`Hatch Spacing (in)${draft.Pattern && !["none", "solid"].includes(draft.Pattern) ? "" : " (hatch patterns only)"}`}>
              <input type="number" step="0.5" min="0" placeholder="screen-constant" disabled={!draft.Pattern || ["none", "solid"].includes(draft.Pattern)} value={draft.PatternSpacingIn ?? ""} onChange={e => setDraft({ ...draft, PatternSpacingIn: e.target.value })} style={inputSm} title="Distance between hatch lines in real inches on the drawing. Blank = a fixed on-screen spacing at every zoom." />
            </Field>
          </div>
        )}
        <datalist id="round-in-presets">{[1, 3, 6, 12].map(v => <option key={v} value={v} />)}</datalist>
        <datalist id="round-sf-presets">{[0.1, 1, 5, 10].map(v => <option key={v} value={v} />)}</datalist>
        {draft.Style === "area" && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 }}>
            <Field label="Round Area To (SF)"><input type="number" step="0.1" min="0" list="round-sf-presets" placeholder="no rounding" value={draft.RoundAreaToSf ?? ""} onChange={e => setDraft({ ...draft, RoundAreaToSf: e.target.value })} style={inputSm} title="Each drawn area is rounded to the nearest increment before any quantity derives from it — 1 = nearest SF, 5 = nearest 5 SF, 0.1 = a tenth. Rounded per shape. Blank = exact." /></Field>
          </div>
        )}
        {draft.Style === "linear" && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 }}>
            <Field label="Round Length To (in)"><input type="number" step="0.25" min="0" list="round-in-presets" placeholder="no rounding" value={draft.RoundToIn ?? ""} onChange={e => setDraft({ ...draft, RoundToIn: e.target.value })} style={inputSm} title='Each drawn run&apos;s measured length is rounded to the nearest increment before any quantity derives from it — 1 = nearest inch, 12 = nearest foot. Blank = exact.' /></Field>
          </div>
        )}
      </div>
    </div>
  );
}
